import { ConfigMarket } from '@App/redux/interfaces'
import { AppDispatch, RootState } from '@App/redux/store'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

export type ImpactLevel = 'low' | 'medium' | 'high' | 'none'

type Country = {
  key: string
  name?: string
}

export type Metric = {
  actual: string | number | null
  forecast: string | number | null
  prior: string | number | null
  unit?: string
}

export type EventMarket = Pick<ConfigMarket, 'title' | 'id' | 'target_currency_code' | 'source_currency_code'>

export type CalendarEvent = {
  id: number
  datetime: string
  date: string
  country: Country
  title: string
  impact: ImpactLevel
  metric: Metric
  retatedMarkets: EventMarket[]
}

const makeMetric = (actual: any, forecast: any, prior: any, unit?: string) => ({
  actual,
  forecast,
  prior,
  unit,
})

const initialState: CalendarEvent[] = []

export const getEvents = createAsyncThunk<
  CalendarEvent[],
  { from: Date; to: Date },
  {
    state: RootState
    dispatch: AppDispatch
  }
>('calendar/getEvents', async ({ from, to }, { getState }) => {
  const state = getState()
  const markets = state.config.markets

  const countries = [
    { key: 'US', name: 'United States' },
    { key: 'BR', name: 'Brazil' },
    { key: 'JP', name: 'Japan' },
    { key: 'GB', name: 'United Kingdom' },
    { key: 'AU', name: 'Australia' },
    { key: 'EU', name: 'Eurozone' },
    { key: 'CH', name: 'Switzerland' },
  ]

  const impacts: ImpactLevel[] = ['high', 'medium', 'low', 'none']

  const titles = [
    'Interest Rate Decision',
    'Consumer Price Index',
    'Employment Report',
    'Industrial Production',
    'Retail Sales',
    'Central Bank Speech',
  ]

  const events: CalendarEvent[] = []

  const fromDate = new Date(from)
  fromDate.setHours(0, 0, 0, 0)

  const toDate = new Date(to)
  toDate.setHours(23, 59, 59, 999)

  const today = new Date()
  const startOfCurrentWeek = new Date(today)
  startOfCurrentWeek.setDate(today.getDate() - ((today.getDay() + 6) % 7))

  const startDate = new Date(startOfCurrentWeek)
  startDate.setDate(startDate.getDate() - 7)

  let globalIndex = 0

  for (let dayOffset = 0; dayOffset < 21; dayOffset++) {
    const date = new Date(startDate)
    date.setDate(startDate.getDate() + dayOffset)

    if (date < fromDate || date > toDate) continue

    const dateStr = date.toISOString().slice(0, 10)

    for (let i = 0; i < 6; i++) {
      const country = countries[(dayOffset + i) % countries.length]
      const impact = impacts[(dayOffset + i) % impacts.length]
      const title = titles[i % titles.length]

      const shuffledMarkets = [...markets].sort(() => Math.random() - 0.5)

      events.push({
        id: 100000 + globalIndex,
        date: dateStr,
        datetime: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 8 + i * 2, 0).toISOString(),
        country,
        title,
        impact,
        metric: makeMetric(`${(i + 1) * 0.5}%`, `${(i + 1) * 0.5}%`, `${(i + 1) * 0.5}%`, '%'),
        retatedMarkets: shuffledMarkets.slice(0, 3).map((m) => ({
          id: m.id,
          title: m.title,
          source_currency_code: m.source_currency_code,
          target_currency_code: m.target_currency_code,
        })),
      })

      globalIndex++
    }
  }

  return events
})

const calendarEventsSlice = createSlice({
  name: 'calendar-events',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getEvents.fulfilled, (_, { payload }) => {
      return payload
    })
  },
})

export default calendarEventsSlice.reducer
